import type { FlowNode } from '../../types/flow-types';
import type { FlowCardDefinition } from './types';
import { deviceActionCard, deviceConditionCards } from './definitions/device-capabilities';
import { mathConditionCard } from './definitions/math-condition';
import { providerConditionCards } from './definitions/provider-conditions';
import { validateCardData } from './schema';
import { presenceConditionCard } from './definitions/presence';
import { setVariableCard } from './definitions/set-variable';
import { variableConditionCard } from './definitions/variable-condition';
import { manualConditionCards } from './definitions/manual-condition';
import { notificationCards } from './definitions/notification';
import { timerCards } from './definitions/timer';
import { sceneResourceCards } from './definitions/scene-resource';
import { groupActionCard } from './definitions/group-action';
import { scriptActionCard } from './definitions/script-action';
import {
	virtualComponentActionCards,
	virtualComponentConditionCard,
	virtualComponentEventCard,
} from './definitions/virtual-components';

export const FLOW_CARD_DEFINITIONS: FlowCardDefinition[] = [
	...deviceConditionCards,
	mathConditionCard,
	deviceActionCard,
	...providerConditionCards,
	setVariableCard,
	presenceConditionCard,
	variableConditionCard,
	...manualConditionCards,
	...notificationCards,
	...timerCards,
	...sceneResourceCards,
	groupActionCard,
	scriptActionCard,
	virtualComponentEventCard,
	virtualComponentConditionCard,
	...virtualComponentActionCards,
];

const cardsByPalette = new Map(
	FLOW_CARD_DEFINITIONS.map((card) => [card.paletteKey || card.type, card]),
);
const cardsByType = new Map<string, FlowCardDefinition>();
for (const card of FLOW_CARD_DEFINITIONS) {
	if (!cardsByType.has(card.type)) cardsByType.set(card.type, card);
}

export function getFlowCardDefinition(
	type: string | undefined,
	paletteKey?: string,
): FlowCardDefinition | undefined {
	if (paletteKey && cardsByPalette.has(paletteKey)) return cardsByPalette.get(paletteKey);
	return type ? cardsByType.get(type as FlowNode['type']) : undefined;
}

export function validateFlowCard(node: FlowNode) {
	const definition = getFlowCardDefinition(node.type, String(node.data.paletteKey || ''));
	if (!definition) return [];
	const data = migrateFlowCardData(node.type, node.data);
	const domainIssues = definition.validate?.(data) ?? [];
	return [
		...validateCardData(definition.schema, data).filter(
			(issue) => !domainIssues.some((domainIssue) => domainIssue.path === issue.path),
		),
		...domainIssues,
	].filter(
		(issue, index, all) =>
			all.findIndex((other) => other.path === issue.path && other.message === issue.message) ===
			index,
	);
}

export function migrateFlowCardData(
	type: string,
	data: Record<string, unknown>,
): Record<string, unknown> {
	const definition = getFlowCardDefinition(type, String(data.paletteKey || ''));
	if (!definition) return data;
	const fromVersion = String(data.cardVersion || '0.0.0');
	const migrated = definition.migrate ? definition.migrate(data, fromVersion) : data;
	return { ...migrated, cardVersion: definition.version };
}
